import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ShoppingCart } from 'lucide-react';
import { useCartAnimationStore } from '../stores/cartAnimationStore';
import type { CartParticle } from '../stores/cartAnimationStore';

// ── Trail Dot ────────────────────────────────────────────────────
interface TrailDotProps {
  startX: number;
  startY: number;
  endDx: number;
  endDy: number;
  delay: number;
  size: number;
  opacity: number;
}

const TrailDot: React.FC<TrailDotProps> = ({ startX, startY, endDx, endDy, delay, size, opacity }) => (
  <div
    className="cart-trail-dot"
    style={{
      left: startX - size / 2,
      top: startY - size / 2,
      width: size,
      height: size,
      opacity,
      animationDelay: `${delay}ms`,
      '--end-x': `${endDx}px`,
      '--end-y': `${endDy}px`,
    } as React.CSSProperties}
  />
);

// ── Sparkle Burst ─────────────────────────────────────────────────
interface SparkleProps {
  x: number;
  y: number;
}

const SparkleBurst: React.FC<SparkleProps> = ({ x, y }) => {
  const count = 8;
  return (
    <>
      {Array.from({ length: count }).map((_, i) => {
        const angle = (i / count) * 360;
        return (
          <div
            key={i}
            className="cart-sparkle"
            style={{
              left: x,
              top: y,
              '--angle': `${angle}deg`,
              '--dist': `${18 + Math.random() * 14}px`,
              animationDelay: `${i * 30}ms`,
            } as React.CSSProperties}
          />
        );
      })}
    </>
  );
};

// ── Main Particle ─────────────────────────────────────────────────
interface ParticleItemProps {
  particle: CartParticle;
  onEnd: (id: string) => void;
}

const ParticleItem: React.FC<ParticleItemProps> = ({ particle, onEnd }) => {
  const { id, startX, startY, productImage } = particle;
  const [endDx, setEndDx] = useState(0);
  const [endDy, setEndDy] = useState(0);
  const [ready, setReady] = useState(false);
  const [showSparkle, setShowSparkle] = useState(false);
  const [sparkleX, setSparkleX] = useState(0);
  const [sparkleY, setSparkleY] = useState(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const cartBtn = document.getElementById('cart-btn');
    if (cartBtn) {
      const rect = cartBtn.getBoundingClientRect();
      const targetX = rect.left + rect.width / 2;
      const targetY = rect.top + rect.height / 2;
      setEndDx(targetX - startX);
      setEndDy(targetY - startY);
      setSparkleX(targetX);
      setSparkleY(targetY);
    } else {
      // fallback: top-right
      const tx = window.innerWidth - 60;
      const ty = 50;
      setEndDx(tx - startX);
      setEndDy(ty - startY);
      setSparkleX(tx);
      setSparkleY(ty);
    }
    setReady(true);

    // Sparkle when particle arrives
    const sparkleTimer = setTimeout(() => setShowSparkle(true), 820);

    timerRef.current = setTimeout(() => {
      onEnd(id);
    }, 1100);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      clearTimeout(sparkleTimer);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!ready) return null;

  return (
    <>
      {/* Trail dots (3 smaller, delayed) */}
      <TrailDot startX={startX} startY={startY} endDx={endDx} endDy={endDy} delay={80} size={12} opacity={0.7} />
      <TrailDot startX={startX} startY={startY} endDx={endDx} endDy={endDy} delay={160} size={8} opacity={0.45} />
      <TrailDot startX={startX} startY={startY} endDx={endDx} endDy={endDy} delay={240} size={5} opacity={0.25} />

      {/* Main particle */}
      <div
        className="cart-fly-particle"
        style={{
          left: startX - 20,
          top: startY - 20,
          '--end-x': `${endDx}px`,
          '--end-y': `${endDy}px`,
        } as React.CSSProperties}
      >
        {productImage ? (
          <img
            src={productImage}
            alt=""
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: '50%',
            }}
          />
        ) : (
          <div style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <ShoppingCart size={16} color="white" />
          </div>
        )}
      </div>

      {/* Sparkle burst when particle arrives */}
      {showSparkle && <SparkleBurst x={sparkleX} y={sparkleY} />}
    </>
  );
};

// ── Root Component ────────────────────────────────────────────────
const CartFlyParticle: React.FC = () => {
  const { particles, removeParticle } = useCartAnimationStore();
  if (particles.length === 0) return null;
  return createPortal(
    <>
      {particles.map((p) => (
        <ParticleItem key={p.id} particle={p} onEnd={removeParticle} />
      ))}
    </>,
    document.body
  );
};

export default CartFlyParticle;
